import type { EditorConfig, NodeKey, SerializedTextNode, Spread } from 'lexical';
import { TextNode } from 'lexical';

import type { CommentablePersonProfile } from 'js/src/client';
import type { DeepReadonly } from 'js/src/libs/consts';

const LEXICAL_NODE_TYPE = 'mention-node';
const LEXICAL_NODE_VERSION = 1;

export type SerializedMentionNode = Spread<
    {
        person: CommentablePersonProfile;
        type: typeof LEXICAL_NODE_TYPE;
        version: typeof LEXICAL_NODE_VERSION;
    },
    SerializedTextNode
>;

export class MentionNode extends TextNode {
    __person: DeepReadonly<CommentablePersonProfile>;

    constructor(person: DeepReadonly<CommentablePersonProfile>, key?: NodeKey) {
        const displayText = person.first_name || person.email;
        super(displayText, key);
        this.__person = person;
    }

    static override getType() {
        return LEXICAL_NODE_TYPE;
    }

    static override clone(node: MentionNode): MentionNode {
        // cloning is used to update one editor state to the next (when the user types more text, for example)
        return new MentionNode(node.__person, node.__key);
    }

    get person() {
        return this.__person;
    }

    override createDOM(config: EditorConfig): HTMLElement {
        const dom = super.createDOM(config);
        dom.className = 'mention';
        return dom;
    }

    static override importJSON(serializedNode: SerializedMentionNode): MentionNode {
        return new MentionNode(serializedNode.person);
    }

    override exportJSON(): SerializedMentionNode {
        return {
            ...super.exportJSON(),
            person: { ...this.__person },
            type: LEXICAL_NODE_TYPE,
            version: LEXICAL_NODE_VERSION,
        };
    }
}

export function $createMentionNode(person: DeepReadonly<CommentablePersonProfile>): MentionNode {
    const mentionNode = new MentionNode(person);
    // 'token' not 'segmented': segmented deletes one word at a time, so a
    // multi-word name would take several backspaces to remove.
    mentionNode.setMode('token');
    return mentionNode;
}
